import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AlertCircle, ChevronLeft, ChevronRight, Lock, NotebookPen, Plus, RefreshCw, Search, X } from 'lucide-react';
import { staffApiService } from '../services/staffApi';
import { staffManagerApiService } from '../services/staffManagerApi';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { StaffAvatar } from '../components/staff/StaffAvatar';
import { HubStat, StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import { NOTE_TYPE_META, StaffNoteCard } from '../components/staff/notes/StaffNoteCard';
import { StaffNoteModal } from '../components/staff/notes/StaffNoteModal';
import { buildSubjectOptions, StaffNoteSubjectPicker, type SubjectOption } from '../components/staff/notes/StaffNoteSubjectPicker';
import type { StaffRosterEntry } from '../types/staff';
import type { StaffDirectoryEntry, StaffNote, StaffNoteStatus, StaffNoteType } from '../types/staffManager';
import { getStaffPerms, STAFF_NOTES_READ_PERMISSION } from '../utils/staffPermissions';
import { cn } from '../utils/cn';

const PAGE_SIZE = 50;
const normalizeUuid = (uuid: string) => uuid.toLowerCase().replace(/-/g, '');

interface SubjectGroup {
  key: string;
  subject: SubjectOption | null;
  label: string;
  discord_id: string | null;
  uuid: string | null;
  notes: StaffNote[];
}

/** Staff Manager · Notes — every Staff Manager Note, grouped by staff member, with filters and deep links. */
export const StaffNotesView = () => {
  useDocumentTitle('Staff Manager Notes');
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);
  const [searchParams, setSearchParams] = useSearchParams();
  const highlight = searchParams.get('highlight');

  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ total: number; results: StaffNote[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<'' | StaffNoteType>('');
  const [status, setStatus] = useState<'' | StaffNoteStatus>('active');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [directory, setDirectory] = useState<StaffDirectoryEntry[]>([]);
  const [roster, setRoster] = useState<StaffRosterEntry[]>(() => staffApiService.getCachedRoster()?.staff ?? []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedSubject, setPickedSubject] = useState<SubjectOption | null>(null);
  const [modal, setModal] = useState<{ mode: 'create'; subject: SubjectOption } | { mode: 'edit'; note: StaffNote } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StaffNote | null>(null);
  const [deleting, setDeleting] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(pickerRef, () => setPickerOpen(false));

  const load = useCallback(
    async (force = false) => {
      if (force) setRefreshing(true);
      setError(null);
      try {
        const res = await staffManagerApiService.getAllNotes({ page, limit: PAGE_SIZE, type: type || undefined, status: status || undefined, q: debouncedQuery || undefined });
        setData({ total: res.total, results: res.results });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load Staff Manager Notes');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, type, status, debouncedQuery],
  );

  useEffect(() => {
    if (!perms.canReadNotes) return;
    setLoading(true);
    void load();
  }, [perms.canReadNotes, load]);

  useEffect(() => {
    if (!perms.canReadNotes) return;
    staffManagerApiService.getDirectory().then((d) => setDirectory(d.staff)).catch(() => undefined);
    staffApiService.getRoster().then((r) => setRoster(r.staff)).catch(() => undefined);
  }, [perms.canReadNotes]);

  useEffect(() => {
    setPage(1);
  }, [type, status, debouncedQuery]);

  useEffect(() => {
    if (!highlight || loading) return;
    const el = document.getElementById(`staff-note-${highlight}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlight, loading, data]);

  const subjects = useMemo(() => buildSubjectOptions(directory, roster), [directory, roster]);
  const subjectLookup = useMemo(() => {
    const byDiscord = new Map<string, SubjectOption>();
    const byUuid = new Map<string, SubjectOption>();
    const byUser = new Map<string, SubjectOption>();
    for (const s of subjects) {
      if (s.discord_id) byDiscord.set(s.discord_id, s);
      if (s.uuid) byUuid.set(normalizeUuid(s.uuid), s);
      if (s.user_id) byUser.set(s.user_id, s);
    }
    return { byDiscord, byUuid, byUser };
  }, [subjects]);

  const groups = useMemo<SubjectGroup[]>(() => {
    const map = new Map<string, SubjectGroup>();
    for (const n of data?.results ?? []) {
      const subject =
        (n.subject_discord_id && subjectLookup.byDiscord.get(n.subject_discord_id)) ||
        (n.subject_uuid && subjectLookup.byUuid.get(normalizeUuid(n.subject_uuid))) ||
        (n.subject_user_id && subjectLookup.byUser.get(n.subject_user_id)) ||
        null;
      const key = subject?.key ?? `raw:${n.subject_discord_id ?? n.subject_uuid ?? n.subject_user_id ?? n.id}`;
      const group = map.get(key) ?? {
        key,
        subject,
        label: subject?.display_name ?? n.subject_display_name ?? n.subject_discord_id ?? n.subject_uuid ?? n.subject_user_id ?? 'Unknown',
        discord_id: subject?.discord_id ?? n.subject_discord_id,
        uuid: subject?.uuid ?? n.subject_uuid,
        notes: [],
      };
      group.notes.push(n);
      map.set(key, group);
    }
    return Array.from(map.values());
  }, [data, subjectLookup]);

  const counts = useMemo(() => {
    const c = { warning: 0, commendation: 0, note: 0 };
    for (const n of data?.results ?? []) c[n.type] += 1;
    return c;
  }, [data]);

  const onSaved = (saved: StaffNote) => {
    setData((prev) => {
      if (!prev) return { total: 1, results: [saved] };
      const idx = prev.results.findIndex((n) => n.id === saved.id);
      if (idx === -1) return { total: prev.total + 1, results: [saved, ...prev.results] };
      const results = prev.results.slice();
      results[idx] = saved;
      return { ...prev, results };
    });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await staffManagerApiService.deleteNote(pendingDelete.id);
      setData((prev) => (prev ? { total: Math.max(0, prev.total - 1), results: prev.results.filter((n) => n.id !== pendingDelete.id) } : prev));
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete the note');
    } finally {
      setDeleting(false);
    }
  };

  if (!perms.canReadNotes) {
    return (
      <div className="glass-card p-8 text-center">
        <Lock className="w-8 h-8 mx-auto mb-2 text-amber-300" />
        <p className="text-glass-300">Staff Manager Notes are restricted.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{STAFF_NOTES_READ_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const pickerModal =
    pickerOpen &&
    createPortal(
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
        <div ref={pickerRef} className="glass-card p-4 md:p-6 rounded-lg w-full max-w-lg max-md:max-w-[95vw] relative">
          <button type="button" onClick={() => setPickerOpen(false)} className="absolute top-3 right-3 text-glass-400 hover:text-white touch-manipulation" aria-label="Close">
            <X />
          </button>
          <h2 className="text-xl font-bold text-white mb-1">Who is the note about?</h2>
          <p className="text-xs text-glass-400 mb-3">Pick a staff member from the Tesseract directory or the steward roster.</p>
          <StaffNoteSubjectPicker directory={directory} roster={roster} value={pickedSubject} onChange={setPickedSubject} />
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setPickerOpen(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button
              type="button"
              className="glass-button"
              disabled={!pickedSubject}
              onClick={() => {
                if (!pickedSubject) return;
                setPickerOpen(false);
                setModal({ mode: 'create', subject: pickedSubject });
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Staff Manager Notes"
        icon={NotebookPen}
        blurb="Confidential notes, warnings and commendations about staff members. Only holders of argus.staff.notes.read can see this page; every read and write is audit-logged."
        nav={<StaffHubNav perms={perms} />}
        actions={
          <>
            {perms.canWriteNotes && (
              <button type="button" onClick={() => setPickerOpen(true)} className="glass-button inline-flex items-center gap-2 text-sm">
                <Plus className="w-4 h-4" />
                New note
              </button>
            )}
            <button type="button" onClick={() => void load(true)} className="glass-button-secondary inline-flex items-center gap-2 text-sm" disabled={refreshing || loading}>
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
              Refresh
            </button>
          </>
        }
        stats={
          data ? (
            <>
              <HubStat label="Notes" value={data.total} sub={status ? `${status} · all pages` : 'all pages'} />
              <HubStat label="Warnings" value={counts.warning} sub="on this page" tone="text-yellow-300" />
              <HubStat label="Commendations" value={counts.commendation} sub="on this page" tone="text-green-300" />
              <HubStat label="Staff covered" value={groups.length} sub="on this page" tone="text-blue-300" />
            </>
          ) : undefined
        }
      />

      <div className="glass-card p-4 md:p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input className="glass-input w-full pl-10" placeholder="Search note text, name, UUID or Discord ID…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search notes" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-glass-200/20 p-0.5" role="group" aria-label="Type">
              {(['', 'note', 'warning', 'commendation'] as const).map((t) => (
                <button
                  key={t || 'all'}
                  type="button"
                  onClick={() => setType(t)}
                  aria-pressed={type === t}
                  className={cn('h-8 px-3 rounded-md text-xs font-semibold transition-colors touch-manipulation', type === t ? 'bg-accent-500/20 text-accent-300' : 'text-glass-400 hover:text-white')}
                >
                  {t ? NOTE_TYPE_META[t].label : 'All types'}
                </button>
              ))}
            </div>
            <select className="glass-select h-9 py-0 text-sm" value={status} onChange={(e) => setStatus(e.target.value as '' | StaffNoteStatus)} aria-label="Status">
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
              <option value="">Any status</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <SkeletonLoader key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="p-10 text-center text-glass-400">
            <NotebookPen className="w-8 h-8 mx-auto mb-2 text-glass-500" />
            No Staff Manager Notes match these filters.
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <section key={g.key} className="space-y-3">
                <header className="flex items-center gap-3">
                  {g.discord_id ? <StaffAvatar discordId={g.discord_id} name={g.label} size="sm" /> : null}
                  {g.discord_id ? (
                    <Link to={`/staff/${g.discord_id}?tab=notes`} className="text-sm font-semibold text-white hover:text-accent-300 transition-colors">
                      {g.label}
                    </Link>
                  ) : g.uuid ? (
                    <Link to={`/player/${g.uuid}`} className="text-sm font-semibold text-white hover:text-accent-300 transition-colors">
                      {g.label}
                    </Link>
                  ) : (
                    <span className="text-sm font-semibold text-white">{g.label}</span>
                  )}
                  <span className="text-xs font-mono text-glass-500 tabular-nums">{g.notes.length}</span>
                </header>
                <div className="space-y-3 md:pl-11">
                  {g.notes.map((n) => (
                    <StaffNoteCard
                      key={n.id}
                      note={n}
                      canWrite={perms.canWriteNotes}
                      highlighted={highlight === n.id}
                      onEdit={(note) => setModal({ mode: 'edit', note })}
                      onDelete={setPendingDelete}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {data && totalPages > 1 && (
          <div className="flex items-center justify-between pt-2 border-t border-glass-200/10">
            <button type="button" className="glass-button-secondary inline-flex items-center gap-1" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="w-4 h-4" /> Newer
            </button>
            <span className="text-xs font-mono text-glass-500">
              page {page} / {totalPages}
            </span>
            <button type="button" className="glass-button-secondary inline-flex items-center gap-1" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Older <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {pickerModal}
      <StaffNoteModal
        isOpen={modal !== null}
        mode={modal?.mode ?? 'create'}
        note={modal?.mode === 'edit' ? modal.note : null}
        subject={modal?.mode === 'create' ? modal.subject : null}
        onClose={() => {
          setModal(null);
          setPickedSubject(null);
          if (highlight) {
            const next = new URLSearchParams(searchParams);
            next.delete('highlight');
            setSearchParams(next, { replace: true });
          }
        }}
        onSaved={onSaved}
      />
      <ConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
        title="Delete this note?"
        message="The note is removed permanently. The deletion itself is audit-logged (without the content)."
        confirmText="Delete"
        isLoading={deleting}
      />
    </div>
  );
};
